import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minRows?: number;
};

const ToolbarButton: React.FC<{
  onClick: () => void;
  isActive?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, isActive, title, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    className={`px-2 py-1 text-sm rounded transition-colors ${
      isActive
        ? 'bg-brand-primary text-white'
        : 'text-ink-secondary hover:text-ink-primary hover:bg-surface-card-hover'
    }`}
  >
    {children}
  </button>
);

export const RichTextEditor: React.FC<Props> = ({ value, onChange, placeholder, minRows = 3 }) => {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    onUpdate({ editor }) {
      const html = editor.getHTML();
      // Treat empty paragraph as empty string
      onChange(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class: 'outline-none min-h-[inherit] tiptap-content',
      },
    },
  });

  if (!editor) return null;

  const minHeight = `${minRows * 1.75}rem`;

  return (
    <div className="w-full bg-surface-app-elevated border border-border-strong rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-brand-primary focus-within:border-brand-primary">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-border-strong bg-surface-app-elevated/80">
        <ToolbarButton
          title="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          title="Strike"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
        >
          <span className="line-through">S</span>
        </ToolbarButton>
        <div className="w-px h-4 bg-surface-app-elevated mx-1" />
        <ToolbarButton
          title="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
        >
          ≡
        </ToolbarButton>
        <ToolbarButton
          title="Ordered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
        >
          №
        </ToolbarButton>
      </div>

      {/* Editor area */}
      <div className="relative px-3 py-2 text-white text-sm" style={{ minHeight }}>
        {editor.isEmpty && placeholder && (
          <span className="absolute top-2 left-3 text-ink-muted pointer-events-none select-none">
            {placeholder}
          </span>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};
